import { ListItem, ListItemButton, styled } from '@mui/material';

import { useNavigate, useParams } from 'react-router';

import { useState } from 'react';

import { TPrototypeShortDto } from '@app-velocity/av-api-v2';
import { ArrowForwardIcon, ClearIcon, OpenIcon } from '@app-velocity/icons';

import { MessageBus } from '@app-velocity/message-bus';

import { useAppContext } from '@app-velocity/config';

import { useScenarioListContext } from '../../hooks/useScenarioListContext';
import { ScenarioRoutePaths } from '../../../../routes/ScenariosRoutePaths';
import { ScenarioListItemText } from './components/ScenarioListItemText';
import { SingleChallengeStatus } from '../../../SingleChallengeStatus';

export type SingleScenarioProps = {
  scenario: TPrototypeShortDto;
};

const StyledScenarioButton = styled('button')({
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  padding: '0 3px',
});

const StyledShareButton = styled(StyledScenarioButton)<{ hovered: boolean }>(
  ({ hovered }) => ({
    svg: {
      fill: hovered ? 'var(--icon-icon-primary-color)' : 'transparent',
    },
  })
);

const StyledOpenButton = styled(StyledScenarioButton)<{ hovered: boolean }>(
  ({ hovered, theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    svg: {
      fill: hovered
        ? 'var(--icon-icon-secondary-color)'
        : 'var(--icon-icon-tertiary-color)',
    },
  })
);

const StyledDeleteButton = styled(StyledScenarioButton)<{ hovered: boolean }>(
  ({ hovered }) => ({
    svg: {
      fill: hovered ? 'var(--error-error-icon-primary-color)' : 'transparent',
    },
  })
);

const StyledListItem = styled(ListItem)(({ theme }) => ({
  padding: 0,
  margin: theme.spacing(-1.5),
  width: 'auto',
}));

const HumanReviewsBadge = styled('div')(({ theme }) => ({
  height: theme.spacing(4),
  minWidth: theme.spacing(4),
  backgroundColor: 'var(--success-success-icon-primary-color)',
  color: 'var(--button-button-primary-text-color)',
  borderRadius: theme.spacing(2),
  fontSize: 10,
  fontWeight: 500,
  textAlign: 'center',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: '0 0 auto',
}));

const Spacer = styled('div')(({ theme }) => ({
  height: theme.spacing(4),
  width: theme.spacing(4),
  flex: '0 0 auto',
}));

const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
  padding: `0 ${theme.spacing(1.5)}`,
  gap: theme.spacing(2)
}));

const messageBus = new MessageBus('system');

export const SingleScenario = ({ scenario }: SingleScenarioProps) => {
  const { workspaceId, teamId } = useParams<{ teamId: string, workspaceId: string }>();
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(false);
  const { AVMLink } = useAppContext();
  const { removeScenario } = useScenarioListContext();

  const openScenario = (e: React.MouseEvent) => {
    e.stopPropagation();

    window.open(`${AVMLink}/scenario/${scenario.shareToken}`);
  };

  const confirmScenarioDelete = (e: React.MouseEvent) => {
    e.stopPropagation();

    messageBus.publish('modal:action-confirmation:open', {
      title: 'prototypes:delete_challenge.title',
      description:
        scenario.statistic?.runsCount && scenario.statistic?.runsCount > 0
          ? 'prototypes:delete_challenge.with_responses_description'
          : 'prototypes:delete_challenge.description',
      confirmButtonText: 'shared:delete',
      cancelButtonText: 'shared:cancel',
      confirmButtonColor: 'error',
      onModalCloseCallback: (decision) => {
        if (decision === 'CANCEL') return;

        removeScenario(scenario.id!);
      },
    });
  };

  const goToDetails = () => {
    if (!workspaceId || !teamId || !scenario.id) return;
    navigate(ScenarioRoutePaths.challengeDetails(teamId, workspaceId, scenario.id));
  };

  const { humanReviewsCount, finalTriggerElement, shareToken } = scenario;

  return (
    <StyledListItem>
      <StyledListItemButton
        onClick={goToDetails}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <SingleChallengeStatus scenario={scenario} />

        <ScenarioListItemText scenario={scenario} />

        <StyledDeleteButton
          type="button"
          onClick={confirmScenarioDelete}
          hovered={hovered}
        >
          <ClearIcon />
        </StyledDeleteButton>
        {shareToken && finalTriggerElement ? (
          <StyledShareButton
            type="button"
            onClick={openScenario}
            hovered={hovered}
          >
            <OpenIcon />
          </StyledShareButton>
        ) : undefined}
        <StyledOpenButton type="button" onClick={goToDetails} hovered={hovered}>
          {humanReviewsCount ? (
            <HumanReviewsBadge>{humanReviewsCount}</HumanReviewsBadge>
          ) : null}
          <ArrowForwardIcon />
          {humanReviewsCount ? <Spacer /> : null}
        </StyledOpenButton>
      </StyledListItemButton>
    </StyledListItem>
  );
};
